import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TBaseOption } from '@r1-frontend/ui-react/components/costs/PurchaseOptions/types';
import { Label } from '@r1-frontend/ui-react/components/label';
import { FullWidthListContainer } from '@r1-frontend/ui-react/experimental/containers';

import { EShopCategory } from '~/src/api/content/shop/getShopItems';
import { useAnalytics } from '~/src/features/full-buy/analytics/context';
import EquipmentInfo from '~/src/features/full-buy/components/EquipmentInfo';
import EquipmentPopup from '~/src/features/full-buy/components/EquipmentPopup';
import { RouterCardsViewWithButton } from '~/src/features/full-buy/containers/EquipmentList/RouterCardsViewWithButton';
import {
    RouterCardsViewWithCarousel,
} from '~/src/features/full-buy/containers/EquipmentList/RouterCardsViewWithCarousel';
import { getPurchaseOptions } from '~/src/features/full-buy/helpers/helpersForProduct/getPurchaseOptions';
import { getLimits } from '~/src/features/full-buy/helpersForProducts';
import { addEquipment } from '~/src/features/full-buy/store/actions/products/thunk/addEquipment';
import { changeEquipmentCount } from '~/src/features/full-buy/store/actions/products/thunk/changeEquipmentCount';
import { selectProductsByCategory } from '~/src/features/full-buy/store/selectors/products/selectProductsByCategory';
import { TProduct } from '~/src/features/full-buy/store/transformShopItem';
import { TState } from '~/src/store';

export type TRouterListProperties = {
    category?: EShopCategory.Routers | EShopCategory.PrivateSectorEquipment,
    withCarousel?: boolean,
}

/**
 * Список роутеров
 */
export const EquipmentList = ({ category = EShopCategory.Routers, withCarousel }: TRouterListProperties): JSX.Element => {
    const analytics = useAnalytics();

    const dispatch = useDispatch();
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const equipments = useSelector((state: TState) => selectProductsByCategory(state, category));

    const limits = useMemo(() => getLimits(equipments, category), [equipments, category]);
    const [currentEquipmentId, setCurrentEquipmentId] = useState<number>();
    const [isInstallmentDisable, setIsInstallmentDisable] = useState(false);

    const currentEquipment: { value: TProduct, options: Record<number, TBaseOption> } | undefined = useMemo(() => {
        if (currentEquipmentId) {
            const value = equipments.find(e => e.id === currentEquipmentId);
            const options = value && getPurchaseOptions(value?.prices, (isInstallmentDisable && limits.installment));
            if (value && options) {
                return { value, options };
            }
        }
    }, [currentEquipmentId, equipments]);

    /* Записывает выбранный продукт в стор */
    const handleAddEquipment = (cost: number) => {
        dispatch(addEquipment(currentEquipment?.value, cost, analytics));
        setIsPopupOpen(false);
    };

    const openAddEquipmentPopup = (equipment: TProduct, isInstallmentDisabled: boolean) => () => {
        setCurrentEquipmentId(equipment.id);
        setIsInstallmentDisable(isInstallmentDisabled);
        setIsPopupOpen(true);
    };

    useEffect(() => {
        if (isPopupOpen && currentEquipment?.value.title) {
            currentEquipment && analytics.equipment.clickAddProduct(
                `type:${category}|name:${currentEquipment.value.title}|`,
            );
        }
    }, [analytics.equipment, currentEquipment?.value.title, isPopupOpen]);

    const handleIncrement = (id: number) => () => {
        dispatch(changeEquipmentCount(id, true, category, analytics));
    };
    const handleDecrement = (id: number) => () => {
        dispatch(changeEquipmentCount(id, false, category, analytics));
    };

    const handleClosePopup = () => {
        setIsPopupOpen(false);
    };

    return (
        <FullWidthListContainer $desktop={16} $mobile={16} $direction={'column'} $wrap={'nowrap'}>
            {withCarousel
                ? <RouterCardsViewWithCarousel
                    handleIncrement={handleIncrement}
                    handleDecrement={handleDecrement}
                    openAddEquipmentPopup={openAddEquipmentPopup}
                    category={category}
                />
                : <RouterCardsViewWithButton
                    handleIncrement={handleIncrement}
                    handleDecrement={handleDecrement}
                    openAddEquipmentPopup={openAddEquipmentPopup}
                    category={category}
                />}

            {!currentEquipment
                ? null
                : (
                    <EquipmentPopup
                        isOpen={isPopupOpen}
                        onClose={handleClosePopup}
                    >
                        <EquipmentInfo
                            category={category}
                            title={currentEquipment.value.title}
                            images={[currentEquipment.value.image, ...currentEquipment.value.images]}
                            labels={currentEquipment.value.labels.map(label => (
                                <Label key={label.id} bgColor={label.color} textColor={label.color_text}>
                                    {label.title}
                                </Label>
                            ))}
                            body={currentEquipment.value.body}
                            characteristics={currentEquipment.value.props}
                            description={currentEquipment.value.description}
                            options={currentEquipment.options}
                            onSelect={handleAddEquipment}
                            onClose={handleClosePopup}
                            quantity={currentEquipment.value.quantity}
                            onIncrement={handleIncrement(currentEquipment.value.id)}
                            onDecrement={handleDecrement(currentEquipment.value.id)}
                        />
                    </EquipmentPopup>
                )}
        </FullWidthListContainer>
    );
};
